import { useMemo } from 'react';

/**
 * Hook to convert timestamps to local time format: "Jan 21, 2025 at 12:37PM"
 * Supports various input formats:
 * - ISO strings (2025-01-21T12:37:00Z, 2025-01-21T15:45:00-08:00)
 * - Date objects
 * - Relative time strings (1 hour ago, 2 days ago) - returned as-is
 * - Formatted date strings (Jan 21, 2025 at 12:37PM)
 * - Time-only strings (12:37PM, 11:45)
 *
 * All parsed timestamps are converted to user's local timezone and formatted consistently.
 */
export const useLocalTimestamp = (timestamp: string | Date) => {
  const formattedTimestamp = useMemo(() => {
    try {
      // Handle Date objects
      if (timestamp instanceof Date) {
        return formatToLocalTime(timestamp);
      }

      // Handle string inputs
      const timestampStr = timestamp.toString().trim();

      // If it's already a relative time (contains "ago") or special cases, return as-is

      // Try to parse the timestamp string
      let date: Date;

      // Check if it's an ISO string
      if (isISOString(timestampStr)) {
        date = new Date(timestampStr);
      }

      // Check if it's just a time (e.g., "12:37PM" or "12:37")
      else if (isTimeOnly(timestampStr)) {
        // For time-only strings, assume today's date
        const today = new Date();
        const timeMatch = timestampStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);

        if (timeMatch) {
          let hours = parseInt(timeMatch[1]);
          const minutes = parseInt(timeMatch[2]);
          const ampm = timeMatch[3]?.toUpperCase();

          // Convert to 24-hour format if needed
          if (ampm === 'PM' && hours !== 12) {
            hours += 12;
          } else if (ampm === 'AM' && hours === 12) {
            hours = 0;
          }

          date = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate(),
            hours,
            minutes
          );
        } else {
          // Fallback: try to parse as-is
          date = new Date(timestampStr);
        }
      }
      // Try to parse any other format
      else {
        date = new Date(timestampStr);
      }

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return timestampStr; // Return original if can't parse
      }

      return formatToLocalTime(date);
    } catch (error) {
      return timestamp.toString(); // Return original on error
    }
  }, [timestamp]);

  return formattedTimestamp;
};

/**
 * Format a Date object to local time string in format: "Jan 21, 2025 at 12:37PM"
 */
function formatToLocalTime(date: Date): string {
  // Get the date part in format "January 21, 2025"
  const datePart = date.toLocaleDateString([], {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Get the time part in format "12:37PM"
  const timePart = date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  // Combine them in the desired format
  return `${datePart} ${timePart}`;
}

/**
 * Check if a string is an ISO date string
 */
function isISOString(str: string): boolean {
  // Enhanced regex to handle various ISO 8601 formats:
  // - 2025-01-21T10:30:00Z (UTC)
  // - 2025-01-21T15:45:00-08:00 (with timezone offset)
  // - 2025-01-21T14:20:00.123Z (with milliseconds)
  // - 2025-01-21T11:15:00+00:00 (GMT format)
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|[+-]\d{2}:\d{2})$/.test(
    str
  );
}

/**
 * Check if a string is time-only format
 */
function isTimeOnly(str: string): boolean {
  return /^\d{1,2}:\d{2}(\s*(AM|PM))?$/i.test(str);
}
